/**
 * The error catalogue, as sentences — `docs/04` §4.3 and `docs/07` §2.
 *
 * Phase 6's second exit criterion is "every rejection code in the taxonomy
 * renders as a specific, actionable toast", and this table is the whole of it.
 * The test that guards it enumerates `ErrorCode` **out of `spec/openapi.yaml`**
 * rather than from a list somebody typed here, because a hand-written list
 * passes while missing exactly the codes its author forgot.
 *
 * Four rules from `docs/07`'s preamble, applied throughout:
 *
 * 1. **Say the number.** Every limit rejection carries its limit and the
 *    submitted value in `meta`, so the copy interpolates them.
 * 2. **Say what to do next**, in the same sentence where possible.
 * 3. **Never blame the user for the venue's mechanics.** `SELF_MATCH`,
 *    `MARKET_OI_LIMIT` and the taker delay are the system working as designed;
 *    they render quietly and factually, never as red failures.
 * 4. **Never throw on an unknown code** (§1.4). An unrecognised `code` renders
 *    the server's `detail` under a title chosen by status class, with
 *    `request_id` appended. That path is tested with a code that does not exist.
 *
 * ## Why a template can fall back to `detail`
 *
 * The copy interpolates `meta`, and `meta`'s exact key names are specified in
 * prose rather than in a schema. When a placeholder does not resolve, this file
 * uses the server's own `detail` instead of rendering a sentence with a hole in
 * it — and `detail` is specified to be *specific*: "size 2.000000 is below the
 * 5.000000 minimum", never "invalid order" (`openapi.Problem`). So the fallback
 * is another specific sentence, not a generic one, which is what makes it safe
 * to have.
 *
 * `kind` is `docs/07`'s visual treatment: **error** persists until dismissed,
 * **warning** and **info** auto-dismiss at 5 s, and **quiet** is not a toast at
 * all — it renders inline, or drives a silent retry, and the caller decides
 * which. A quiet code still has specific copy; it just does not interrupt.
 */

import { HttpError, type Problem } from '@/api/http/problem'

export type ToastKind = 'error' | 'warning' | 'info' | 'quiet'

/** The bracketed affordances `docs/07` §2 offers. Each has a caller in
 *  `trading/order-controller.ts`; there are no spare members. */
export type ErrorAction = 'SIGN_IN' | 'ENABLE_TRADING' | 'CANCEL_ALL' | 'SUPPORT'

export interface ErrorPresentation {
  code: string
  kind: ToastKind
  /** Two or three words. The toast's bold line. */
  title: string
  /** The sentence. Interpolated from `meta`, or the server's `detail`. */
  body: string
  /** `request_id`, rendered in the mono row, for the codes worth quoting it on. */
  detail?: string
  action?: ErrorAction
  /** True only for a code this client has never heard of (§1.4). */
  unknown: boolean
}

interface Entry {
  kind: ToastKind
  title: string
  /** `{placeholder}` names are `meta` keys. Every one must resolve or `detail` wins. */
  body: string
  action?: ErrorAction
  /** Chosen by a field of `meta` rather than by the code alone. */
  variants?: { on: string; cases: Record<string, Omit<Entry, 'variants'>> }
  /** Append `request_id` to the toast. Reserved for the ones support will ask for. */
  withRequestId?: boolean
}

/**
 * The catalogue. Every member of `openapi`'s `ErrorCode` enum appears exactly
 * once, and `order-errors.test.ts` fails the build if that stops being true.
 *
 * Three of these are **not** in `docs/07` §2 and were written here against
 * §4.3's own semantics: `SESSION_KEY_REVOKED`, `POSITION_NOT_FOUND` and
 * `UNSUPPORTED_MEDIA_TYPE`. Recorded in register §F22e — the copy document is a
 * draft and the enum is authoritative, so the gap is the copy's.
 */
const CATALOGUE: Record<string, Entry> = {
  // --- authentication and identity ----------------------------------------
  UNAUTHENTICATED: {
    kind: 'info',
    title: 'Sign in to continue',
    body: 'Your order was not placed. Signing in costs one signature and no gas.',
    action: 'SIGN_IN',
  },
  SESSION_EXPIRED: {
    kind: 'info',
    title: 'Session expired',
    body: 'Sign in again — your trading key is unaffected.',
    action: 'SIGN_IN',
  },
  SESSION_KEY_INVALID: {
    kind: 'warning',
    title: 'Trading key revoked',
    body: 'Your trading key was revoked. Enable trading again to continue.',
    action: 'ENABLE_TRADING',
  },
  SESSION_KEY_EXPIRED: {
    // No toast: the user is still signed in and needs one popup, not two. The
    // controller re-registers and resends (§4.3, docs/07 §2).
    kind: 'quiet',
    title: 'Trading key expired',
    body: 'Re-registering your trading key.',
  },
  SESSION_KEY_REVOKED: {
    kind: 'warning',
    title: 'Trading key revoked',
    body: 'That trading key was revoked. Enable trading again to continue.',
    action: 'ENABLE_TRADING',
  },
  // A key has no spending limit since docs/06 §F75, so this code no longer
  // means "the key is used up" and re-arming would not clear it: it is the key
  // asked to do something outside its scope.
  SESSION_KEY_SCOPE: {
    kind: 'warning',
    title: 'Not allowed with this key',
    body: "This trading key isn't allowed to do that.",
  },
  SESSION_KEY_LIMIT: {
    kind: 'warning',
    title: 'Too many trading keys',
    body: 'You have 5 active trading keys. Revoke one in Settings to add another.',
  },
  SIGNATURE_INVALID: {
    kind: 'error',
    title: "Signature didn't verify",
    body: "We couldn't verify that signature. Try again — if it keeps happening, reload the page.",
    withRequestId: true,
  },
  NONCE_REUSED: {
    // The plain replay case is quiet: the controller re-signs with a fresh
    // nonce and only surfaces it if it repeats. A clock skew is worth saying.
    kind: 'quiet',
    title: 'Retrying',
    body: 'That signature was already used. Re-signing.',
    variants: {
      on: 'clock_skew_ms',
      cases: {
        '*': {
          kind: 'warning',
          title: 'Clock out of sync',
          body: 'Your clock is {clock_skew_ms}ms off ours. Re-syncing — try again in a second.',
        },
      },
    },
  },
  GEO_BLOCKED: {
    kind: 'error',
    title: 'Not available here',
    body:
      "UpTik isn't available in your location. Your USDC never left your wallet, and your open positions will still settle.",
  },
  ACCOUNT_RESTRICTED: {
    kind: 'error',
    title: 'Account restricted',
    body: 'Your account is {account_status}.',
    action: 'SUPPORT',
  },

  // --- market state --------------------------------------------------------
  MARKET_NOT_FOUND: {
    kind: 'quiet',
    title: 'Market not found',
    body: "That market doesn't exist. Re-checking the live market.",
  },
  MARKET_NOT_OPEN: {
    kind: 'warning',
    title: 'Market closed',
    body: 'That market has closed. The next one is already trading.',
  },
  MARKET_FIXING: {
    kind: 'info',
    title: 'Strike forming',
    body: 'The strike is still forming. Trading opens when it is fixed.',
  },
  MARKET_RESOLVING: {
    kind: 'info',
    title: 'Market settling',
    body: 'This market is settling. The next one opens shortly.',
  },
  MARKET_HALTED: {
    kind: 'error',
    title: 'Trading halted',
    body: 'Trading is halted. You can still cancel orders.',
  },
  MARKET_VOIDED: {
    kind: 'warning',
    title: 'Market voided',
    body: 'This market was voided. Every order is refunded at cost.',
  },
  CONFIG_STALE: {
    kind: 'quiet',
    title: 'Config out of date',
    body: 'Re-reading the exchange configuration and resending.',
  },

  // --- order validation and risk ------------------------------------------
  TICK_SIZE_INVALID: {
    kind: 'error',
    title: 'Price off the tick',
    body: 'Price must be a whole number of cents between 1 and 99.',
  },
  // Since §F117 there is no collar; what is left is a market order whose
  // signed amounts imply a price outside 1–99¢ (`meta.implied_price_cents`).
  PRICE_OUT_OF_RANGE: {
    kind: 'warning',
    title: 'Price out of range',
    body: 'The signed amounts imply {implied_price_cents}¢, outside 1–99¢.',
  },
  MIN_SIZE: {
    kind: 'warning',
    title: 'Order too small',
    body: 'Minimum order is {min_order_size} shares. You entered {submitted}.',
  },
  MAX_SIZE: {
    kind: 'warning',
    title: 'Order too large',
    body: 'Maximum order is {max_order_size} shares.',
  },
  INSUFFICIENT_BALANCE: {
    kind: 'warning',
    title: 'Not enough balance',
    body: 'This order needs {required} USDC and you have {available}.',
  },
  POSITION_LIMIT: {
    kind: 'warning',
    title: 'Position cap reached',
    body: 'You can hold {headroom} more shares in this market.',
  },
  MARKET_OI_LIMIT: {
    kind: 'info',
    title: 'Market at capacity',
    body: "This market is at capacity — {market_oi_remaining_shares} shares left. It's a safety cap, not a fault.",
  },
  SELF_MATCH: {
    kind: 'quiet',
    title: 'Self-match prevented',
    body: 'It crossed your own resting order, so it was cancelled and nothing was filled.',
  },
  POST_ONLY_WOULD_CROSS: {
    kind: 'warning',
    title: 'Post-only would cross',
    body: 'A post-only order at {price}¢ would have taken liquidity. Re-price or turn post-only off.',
  },
  REDUCE_ONLY_WOULD_OPEN: {
    kind: 'warning',
    title: 'Nothing to close',
    body: 'You have nothing to close in this market.',
  },
  /**
   * B7.5, §F44. **A user of this client should never see this**, and that is
   * the point of the sentence rather than an excuse for a vague one.
   *
   * `domain/sell-shape.ts` rewrites an uncovered sell into a buy of the
   * complement before it is signed, so a browser reaching this code means the
   * rewrite did not run — a stale tab against a venue that has since gained a
   * chain, or a position that moved between the split and the sequencer. The
   * remedy is the same either way and is one word: place the buy instead. The
   * copy names it, because `meta.complement_outcome` and
   * `meta.complement_price_cents` carry exactly that order and the venue put
   * them there so this sentence could be specific.
   */
  UNCOVERED_SELL: {
    kind: 'warning',
    title: "You don't hold these shares",
    body: 'Selling {size} needs shares you already own, and you have {covered}. Buy {complement_price_cents}¢ {complement_outcome} instead — same payoff.',
  },
  MAX_OPEN_ORDERS: {
    kind: 'warning',
    title: 'Too many open orders',
    body: 'You have {count} open orders — the limit is {limit}.',
    action: 'CANCEL_ALL',
  },
  INVALID_PARAMETER: {
    kind: 'error',
    // Deliberately *not* "That didn't go through", which is the title the
    // unknown-code path uses. A known code sharing the generic title is
    // indistinguishable from a code this client has never heard of, and
    // `order-errors.test.ts` fails the build on exactly that collision.
    title: 'Malformed request',
    body: 'Something in that request was malformed ({field}). Reload if it persists.',
    withRequestId: true,
  },
  RANGE_TOO_WIDE: {
    kind: 'quiet',
    title: 'Range too wide',
    body: 'That request covered too long a span and was paged.',
  },
  INVALID_CURSOR: {
    kind: 'quiet',
    title: 'Cursor expired',
    body: 'Restarting that list from the beginning.',
  },
  // §F34. This client does not call `POST /orders/batch` in v1 — which is
  // precisely why the code sat in `docs/04` §4.3's "complete catalogue" and in
  // `openapi`'s own 422 example while being absent from `openapi`'s enum for
  // months. The copy exists because the catalogue is closed and complete in
  // both directions, not because a screen renders it today.
  //
  // No placeholder: a wholly-rejected batch carries its reasons in `errors[]`,
  // one per index, and there is no single number to say. The sentence points at
  // them rather than inventing an aggregate.
  BATCH_ALL_REJECTED: {
    kind: 'error',
    title: 'Batch rejected',
    body: 'None of those orders were accepted. Each one has its own reason — check them and resubmit.',
    withRequestId: true,
  },

  // --- order management ----------------------------------------------------
  ORDER_NOT_FOUND: {
    kind: 'quiet',
    title: 'Order not found',
    body: 'That order is no longer on the book. Re-reading your orders.',
  },
  ORDER_NOT_CANCELLABLE: {
    // **Treated as success.** The user asked for the order not to be resting and
    // it is not resting. A red error because it filled 4 ms before the click is
    // technically accurate and practically wrong (§7.5).
    kind: 'quiet',
    title: 'Already done',
    body: 'That order was already {status} when the cancel arrived.',
  },
  POSITION_NOT_FOUND: {
    kind: 'quiet',
    title: 'No position',
    body: 'You hold no position in that market. Re-reading your positions.',
  },
  DUPLICATE_CLIENT_ORDER_ID: {
    kind: 'quiet',
    title: 'Duplicate order id',
    body: 'That order identity was already used. Retrying with a new one.',
  },
  IDEMPOTENCY_KEY_REUSED: {
    kind: 'quiet',
    title: 'Already in flight',
    body: 'That request is already running. Waiting for its result.',
  },
  IDEMPOTENCY_KEY_EXPIRED: {
    kind: 'warning',
    title: 'Too old to retry',
    body: 'That order is too old to retry — the market has moved on. Check the book and re-price.',
  },

  // --- protocol, quota, platform -------------------------------------------
  RATE_LIMITED: {
    kind: 'warning',
    title: 'Slow down a moment',
    body: 'Try again in {retry_after}s.',
    variants: {
      on: 'bucket',
      cases: {
        FINAL_SECONDS: {
          kind: 'warning',
          title: 'Final-seconds limit',
          body: 'Order limit reached for the last seconds of this market. {notional_remaining} USDC left.',
        },
      },
    },
  },
  PAYLOAD_TOO_LARGE: {
    kind: 'quiet',
    title: 'Request too large',
    body: 'That request was too large to send.',
  },
  UNSUPPORTED_MEDIA_TYPE: {
    kind: 'quiet',
    title: 'Wrong content type',
    body: 'That request was sent in a format the API does not accept.',
  },
  INTERNAL: {
    kind: 'error',
    // Same collision as `INVALID_PARAMETER`, at the other end of the status
    // range: `docs/07`'s copy for `INTERNAL` reads "Something broke on our
    // side", which is also the generic 5xx title. The specific one says the bit
    // that matters to somebody who just clicked Buy — whether their order
    // exists.
    title: 'Order may not have been placed',
    body: 'Your order may not have been placed. Check your open orders before retrying.',
    withRequestId: true,
  },
  MAINTENANCE: {
    kind: 'error',
    title: 'Down for maintenance',
    body: 'UpTik is down for maintenance and will be back shortly.',
  },
  SERVICE_UNAVAILABLE: {
    kind: 'error',
    title: 'Exchange unreachable',
    body: "We're having trouble reaching part of the exchange. Retrying.",
  },

  // --- money ---------------------------------------------------------------
  //
  // **Empty since §F28n, and that is the whole category.** `WITHDRAWAL_LIMIT`,
  // `WITHDRAWAL_UNDER_REVIEW`, `WITHDRAWAL_NOT_CANCELLABLE` and
  // `DEPOSIT_NOT_CREDITED` went with the endpoints that emitted them: the money
  // never leaves the user's own wallet, so there is nothing to deposit and
  // nothing to withdraw.
  //
  // Deleted rather than left as dead copy, because `order-errors.test.ts`
  // asserts this catalogue and `openapi`'s `ErrorCode` enum are equal **in both
  // directions** — copy for a code the server cannot emit is a code this client
  // believes in for nothing.

  // --- settlement ----------------------------------------------------------
  SETTLEMENT_PENDING: {
    kind: 'quiet',
    title: 'Settling',
    body: 'This market has not resolved yet. Waiting for the settlement frame.',
  },

  // --- usernames (§F136, docs/07 §1e) --------------------------------------
  // Quiet, all three: the choose-a-username window answers each inline,
  // beside the field the user is typing in, and none is a trading failure.
  DISPLAY_NAME_TAKEN: {
    kind: 'quiet',
    title: 'Username taken',
    body: 'Someone just took that username. Try another.',
  },
  DISPLAY_NAME_ALREADY_SET: {
    kind: 'quiet',
    title: 'Username already set',
    body: "Your username is {display_name}, and usernames can't be changed.",
  },
  DISPLAY_NAME_FUNDS_REQUIRED: {
    kind: 'quiet',
    title: 'Add funds first',
    body: 'Add funds to your trading balance, then choose a username.',
  },
  CHAT_DISPLAY_NAME_REQUIRED: {
    kind: 'quiet',
    title: 'Choose a username first',
    body: 'Chat messages are shown under your username. Choose one to start chatting.',
  },
}

/** Every code this client has copy for. The test compares it with the spec. */
export function knownErrorCodes(): readonly string[] {
  return Object.keys(CATALOGUE)
}

/**
 * A `Problem` (or any thrown value) → what to put on screen.
 *
 * Never throws, whatever it is handed. A `NetworkError`, a `null`, a string, an
 * error code invented after this build shipped — all of them come back as an
 * `ErrorPresentation` with a specific title and a usable body, because the
 * alternative in a trading client is an unhandled rejection during order entry.
 */
export function presentError(error: unknown): ErrorPresentation {
  const problem = problemOf(error)
  const requestId = error instanceof HttpError ? error.requestId : null
  const code = typeof problem?.code === 'string' ? problem.code : null
  const meta = metaOf(problem)

  if (code === null) {
    // No problem body at all — a network failure, or a response that was not
    // `problem+json`. The status class is all there is to choose a title by.
    const status = error instanceof HttpError ? error.status : 0
    return {
      code: 'NETWORK',
      kind: 'error',
      title: titleForStatus(status),
      body: detailOf(problem) ?? messageOf(error),
      ...(requestId ? { detail: requestId } : {}),
      unknown: true,
    }
  }

  const entry = resolveEntry(CATALOGUE[code], meta)
  if (!entry) {
    // §1.4's forward-compatibility requirement, and the one path that must never
    // throw: a code minted after this bundle was built renders the server's own
    // `detail` under a title chosen by status class, with `request_id` appended.
    const status = typeof problem?.status === 'number' ? problem.status : 0
    return {
      code,
      kind: status >= 500 ? 'error' : 'warning',
      title: titleForStatus(status),
      body: detailOf(problem) ?? 'That request did not go through.',
      ...(requestId ? { detail: requestId } : {}),
      unknown: true,
    }
  }

  const interpolated = interpolate(entry.body, meta)
  return {
    code,
    kind: entry.kind,
    title: entry.title,
    // A template with an unresolved placeholder is replaced by the server's
    // `detail`, which is specified to name the actual numbers involved.
    body: interpolated ?? detailOf(problem) ?? entry.body.replace(/\{[^}]+\}/g, '…'),
    ...(entry.withRequestId && requestId ? { detail: requestId } : {}),
    ...(entry.action ? { action: entry.action } : {}),
    unknown: false,
  }
}

// ---------------------------------------------------------------------------

/**
 * Pick a variant when `meta` decides which sentence applies.
 *
 * Two shapes: an exact match on a `meta` value (`RATE_LIMITED`'s `bucket`), and
 * `'*'` — "this key is present at all", which is
 * how `NONCE_REUSED` distinguishes a clock skew from a plain replay.
 */
function resolveEntry(entry: Entry | undefined, meta: Record<string, unknown>): Entry | undefined {
  if (!entry?.variants) return entry
  const value = meta[entry.variants.on]
  if (value === undefined || value === null) return entry
  const exact = entry.variants.cases[String(value)]
  if (exact) return exact
  return entry.variants.cases['*'] ?? entry
}

/** `null` when any placeholder is unresolved — the caller falls back to `detail`. */
function interpolate(template: string, meta: Record<string, unknown>): string | null {
  let missing = false
  const out = template.replace(/\{([a-z0-9_]+)\}/gi, (_match, key: string) => {
    const value = meta[key]
    if (value === undefined || value === null || value === '') {
      missing = true
      return ''
    }
    return String(value)
  })
  return missing ? null : out
}

function titleForStatus(status: number): string {
  return status >= 500 || status === 0 ? 'Something broke on our side' : "That didn't go through"
}

function problemOf(error: unknown): Problem | null {
  if (error instanceof HttpError) return error.problem
  if (typeof error === 'object' && error !== null && 'code' in error) return error as Problem
  return null
}

function metaOf(problem: Problem | null): Record<string, unknown> {
  const meta = (problem as { meta?: unknown } | null)?.meta
  return typeof meta === 'object' && meta !== null ? (meta as Record<string, unknown>) : {}
}

function detailOf(problem: Problem | null): string | null {
  const detail = (problem as { detail?: unknown } | null)?.detail
  return typeof detail === 'string' && detail.length > 0 ? detail : null
}

function messageOf(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'The request did not reach the exchange. Check your connection and try again.'
}
